import * as THREE from 'three';
import { Asteroid } from '../sector';
import type { NpcBrain, NpcBrainContext } from './brain';
import { applyThrust, faceTarget, holdPosition, steerToward } from './brain';

type MinerState =
  | 'approach'
  | 'mining'
  | 'cooldown'
  | 'combat'
  | 'returning'
  | 'finished';

const MINE_RANGE = 22;
const TARGET_SEARCH_RANGE_SQ = 1000 * 1000; // miners ignore rocks farther than 1000u
const MINE_BREAK_RANGE = MINE_RANGE * 1.6;  // re-approach if target drifts past this
const CRUISE_SPEED = 40;
const APPROACH_SPEED = 60;
const ACCEL = 2.0;
// Doubled (was 3.0) so a miner reorients on the player faster the
// instant it gets attacked — reads as a panicked snap-around rather
// than a slow roll.
const TURN_RATE = 6.0;
const MINING_DPS = 0.9;
const COOLDOWN_TIME = 0.45;
const BEAM_EMIT_OFFSET = new THREE.Vector3(0, -0.1, -1.6);
const STATION_DESPAWN_RANGE = 120;

// Flee behaviour: miners are not fighters. When attacked they bug out at
// max throttle along the away-from-player vector and only return fire if
// the target happens to drift into their forward cone — they never alter
// course to shoot, which is what produced the old "circle the target"
// pattern. Accel + turn doubled so the panic reaction kicks in on the
// same frame the player takes the first shot, instead of a slow drift.
const FLEE_SPEED = 64;
const FLEE_ACCEL = 5.2;
const FLEE_TURN = 7.2;
const FLEE_LOSE_RANGE = 900;
// Vertical bias on the flee direction so a chase doesn't always settle
// into a perfectly horizontal stern chase.
const FLEE_VERTICAL_LIFT = 0.18;

// Bumped from 0.65s so a panicked miner sprays back at a useful rate
// while fleeing — still clearly slower than a guard so the two roles
// read distinctly, no longer feels like one shot every two seconds.
const FIRE_INTERVAL = 0.24;
const FIRE_ANGLE = 0.9;
const FIRE_MIN_DIST = 60;
const FIRE_MAX_DIST = 360;
const PROJECTILE_SPEED = 150;
// Doubled (was 0.35) — a panicked miner now actually punches through
// the player's hull at a noticeable rate. Still well below guard
// damage so the role distinction holds.
const MINER_DAMAGE = 0.7;
const MUZZLE_OFFSET = new THREE.Vector3(0, 0, -2.0);

// Snapfire: a brief panic-shot phase that interrupts the flee. The miner
// turns toward the player, throttles down (but doesn't fully stop — that
// would be an easy target), fires a short burst, then resumes running.
// Cooldown is randomised so chasers can't pre-empt the timing, and we
// gate snapfire by range so far-away miners don't waste shots.
const SNAPFIRE_INTERVAL_MIN = 3.5;
const SNAPFIRE_INTERVAL_MAX = 7.0;
const SNAPFIRE_DURATION_MIN = 0.55;
const SNAPFIRE_DURATION_MAX = 0.95;
const SNAPFIRE_MAX_RANGE = 320;
const SNAPFIRE_THRUST_FACTOR = 0.35;
// Doubled (was 5.0) so the snapfire wheel-and-shoot hits the firing
// cone faster — reaction reads as immediate, not telegraphed.
const SNAPFIRE_TURN = 10.0;
const SNAPFIRE_FIRE_CONE = 0.86;
// Snapfire window is short (≤ 1s) and meant to feel scary; cadence
// sits a hair behind the player so a counterattack is a real threat
// without overwhelming the player.
const SNAPFIRE_FIRE_INTERVAL = 0.16;

const _muzzleWorld = new THREE.Vector3();
const _shotDir = new THREE.Vector3();
const _toPlayer = new THREE.Vector3();
const _away = new THREE.Vector3();
const _aimAway = new THREE.Vector3();
const _forward = new THREE.Vector3();

export interface MinerBrainConfig {
  readonly maxAsteroidsToMine: number;
}

export class MinerBrain implements NpcBrain {
  finished = false;
  private state: MinerState = 'approach';
  private previousWorkState: MinerState = 'approach';
  private hostile = false;
  private target: Asteroid | null = null;
  private asteroidsMined = 0;
  private cooldownTimer = 0;
  private fireCooldown = 0;
  private snapfireRemaining = 0;
  private snapfireCooldown: number;
  private readonly beamFromWorld = new THREE.Vector3();
  private readonly targetWorld = new THREE.Vector3();

  constructor(
    private readonly ctx: NpcBrainContext,
    private readonly config: MinerBrainConfig
  ) {
    // Stagger the first snapfire so a freshly-engaged group of miners
    // doesn't all wheel and fire on the same frame.
    this.snapfireCooldown =
      SNAPFIRE_INTERVAL_MIN +
      Math.random() * (SNAPFIRE_INTERVAL_MAX - SNAPFIRE_INTERVAL_MIN);
  }

  isHostile(): boolean {
    return this.hostile;
  }

  onAttackedByPlayer(): void {
    this.hostile = true;
    this.enterCombat();
  }

  clearHostility(): void {
    this.hostile = false;
    if (this.state === 'combat') this.exitCombatToWork();
  }

  update(dt: number): void {
    this.fireCooldown = Math.max(0, this.fireCooldown - dt);

    switch (this.state) {
      case 'approach':
        this.updateApproach(dt);
        break;
      case 'mining':
        this.updateMining(dt);
        break;
      case 'cooldown':
        this.updateCooldown(dt);
        break;
      case 'combat':
        this.updateCombat(dt);
        break;
      case 'returning':
        this.updateReturning(dt);
        break;
      case 'finished':
        break;
    }
  }

  dispose(): void {
    if (this.ctx.beam) this.ctx.beam.hide();
    this.ctx.claims.release(this.ctx.ship);
  }

  private enterCombat(): void {
    if (this.state === 'combat' || this.state === 'finished') return;
    // Remember what we were doing so we can return to it.
    if (this.state !== 'returning') {
      this.previousWorkState = this.state;
    }
    this.ctx.beam?.hide();
    this.ctx.claims.release(this.ctx.ship);
    this.state = 'combat';
  }

  private exitCombatToWork(): void {
    if (this.asteroidsMined >= this.config.maxAsteroidsToMine) {
      this.state = 'returning';
    } else if (this.previousWorkState === 'mining') {
      // Don't resume mid-mining; re-approach a new target.
      this.target = null;
      this.state = 'approach';
    } else {
      this.state = 'approach';
    }
  }

  private updateApproach(dt: number): void {
    if (!this.target || !this.target.isAlive) {
      this.pickTarget();
    }
    if (!this.target) {
      this.enterReturning();
      return;
    }

    this.target.object3D.getWorldPosition(this.targetWorld);
    const distance = steerToward(
      this.ctx.ship,
      this.targetWorld,
      dt,
      APPROACH_SPEED,
      ACCEL,
      TURN_RATE
    );

    const reach = this.target.collisionRadius + MINE_RANGE;
    if (distance <= reach) {
      this.state = 'mining';
    }
  }

  private updateMining(dt: number): void {
    if (!this.target || !this.target.isAlive) {
      this.asteroidsMined++;
      this.ctx.claims.release(this.ctx.ship);
      this.target = null;
      this.ctx.beam?.hide();
      this.enterCooldown();
      return;
    }

    this.target.object3D.getWorldPosition(this.targetWorld);

    // Ring/belt rocks orbit with their parent planet — if the target has
    // drifted out of range, drop back to approach so the beam doesn't stretch
    // across the map. Reuses the same target (no new claim, no pickTarget).
    const distance = this.targetWorld.distanceTo(this.ctx.ship.object3D.position);
    if (distance > this.target.collisionRadius + MINE_BREAK_RANGE) {
      this.ctx.beam?.hide();
      this.state = 'approach';
      return;
    }

    holdPosition(this.ctx.ship, this.targetWorld, dt, TURN_RATE);

    const destroyed = this.target.takeDamage(MINING_DPS * dt);

    const beam = this.ctx.beam;
    if (beam) {
      this.ctx.ship.object3D.updateMatrixWorld();
      this.beamFromWorld
        .copy(BEAM_EMIT_OFFSET)
        .applyMatrix4(this.ctx.ship.object3D.matrixWorld);
      beam.setEndpoints(this.beamFromWorld, this.targetWorld);
      beam.tick(dt);
    }

    if (destroyed) {
      this.asteroidsMined++;
      this.ctx.claims.release(this.ctx.ship);
      this.ctx.explosions.spawnDestruction(
        this.targetWorld,
        this.target.collisionRadius,
        this.target.baseTint
      );
      this.target.markForRemoval();
      this.target = null;
      this.ctx.beam?.hide();
      this.enterCooldown();
    }
  }

  private updateCooldown(dt: number): void {
    holdPosition(
      this.ctx.ship,
      this.ctx.ship.object3D.position.clone().add(new THREE.Vector3(0, 0, -1)),
      dt,
      TURN_RATE
    );
    this.cooldownTimer -= dt;
    if (this.cooldownTimer <= 0) {
      if (this.asteroidsMined >= this.config.maxAsteroidsToMine) {
        this.enterReturning();
      } else {
        this.state = 'approach';
      }
    }
  }

  /** Flee with periodic snapfire. The miner is mostly running, but every
   *  few seconds it briefly turns and fires before resuming the run — they
   *  never commit to circle-strafing. Snapfire windows are randomised so
   *  groups don't all wheel and shoot on the same tick. */
  private updateCombat(dt: number): void {
    const player = this.ctx.getPlayerShip();
    if (!player) {
      this.hostile = false;
      this.exitCombatToWork();
      return;
    }

    const playerPos = player.object3D.position;
    const shipPos = this.ctx.ship.object3D.position;

    _toPlayer.copy(playerPos).sub(shipPos);
    const distance = _toPlayer.length();

    if (distance > FLEE_LOSE_RANGE) {
      this.hostile = false;
      this.exitCombatToWork();
      return;
    }

    this.snapfireCooldown = Math.max(0, this.snapfireCooldown - dt);
    this.snapfireRemaining = Math.max(0, this.snapfireRemaining - dt);

    // Open a snapfire window when the cooldown expires AND the player is
    // close enough that the shots have a chance of mattering.
    if (
      this.snapfireRemaining <= 0 &&
      this.snapfireCooldown <= 0 &&
      distance < SNAPFIRE_MAX_RANGE
    ) {
      this.snapfireRemaining =
        SNAPFIRE_DURATION_MIN +
        Math.random() * (SNAPFIRE_DURATION_MAX - SNAPFIRE_DURATION_MIN);
      this.snapfireCooldown =
        SNAPFIRE_INTERVAL_MIN +
        Math.random() * (SNAPFIRE_INTERVAL_MAX - SNAPFIRE_INTERVAL_MIN);
    }

    if (this.snapfireRemaining > 0) {
      this.runSnapfire(dt, distance, playerPos);
    } else {
      this.runFlee(dt, distance, playerPos, shipPos);
    }
  }

  private runFlee(
    dt: number,
    distance: number,
    playerPos: THREE.Vector3,
    shipPos: THREE.Vector3
  ): void {
    if (distance < 1e-3) {
      _away.set(0, 0, 1);
    } else {
      _away.copy(shipPos).sub(playerPos).multiplyScalar(1 / distance);
      _away.y += FLEE_VERTICAL_LIFT;
      _away.normalize();
    }

    _aimAway.copy(shipPos).addScaledVector(_away, 1000);
    faceTarget(this.ctx.ship, _aimAway, dt, FLEE_TURN);

    _forward.set(0, 0, -1).applyQuaternion(this.ctx.ship.object3D.quaternion);
    applyThrust(this.ctx.ship, _forward, dt, FLEE_SPEED, FLEE_ACCEL);

    // Opportunistic shot if the chaser drifted into our forward cone — we
    // don't yaw to acquire here, that's snapfire's job.
    const toPlayerNorm = _toPlayer.multiplyScalar(1 / Math.max(distance, 1e-3));
    const aligned = _forward.dot(toPlayerNorm) >= FIRE_ANGLE;
    if (
      aligned &&
      distance > FIRE_MIN_DIST &&
      distance < FIRE_MAX_DIST &&
      this.fireCooldown <= 0
    ) {
      this.fire(playerPos);
      this.fireCooldown = FIRE_INTERVAL;
    }
  }

  /** Brief panic counterattack: face the player, slow forward thrust to
   *  ~35% (still moving so we're not a sitting duck), fire when aligned. */
  private runSnapfire(
    dt: number,
    distance: number,
    playerPos: THREE.Vector3
  ): void {
    faceTarget(this.ctx.ship, playerPos, dt, SNAPFIRE_TURN);
    _forward.set(0, 0, -1).applyQuaternion(this.ctx.ship.object3D.quaternion);
    applyThrust(
      this.ctx.ship,
      _forward,
      dt,
      FLEE_SPEED * SNAPFIRE_THRUST_FACTOR,
      FLEE_ACCEL
    );

    const toPlayerNorm = _toPlayer.multiplyScalar(1 / Math.max(distance, 1e-3));
    const aligned = _forward.dot(toPlayerNorm) >= SNAPFIRE_FIRE_CONE;
    if (
      aligned &&
      distance > FIRE_MIN_DIST &&
      distance < FIRE_MAX_DIST &&
      this.fireCooldown <= 0
    ) {
      this.fire(playerPos);
      this.fireCooldown = SNAPFIRE_FIRE_INTERVAL;
    }
  }

  private updateReturning(dt: number): void {
    const distance = steerToward(
      this.ctx.ship,
      this.ctx.stationPos,
      dt,
      CRUISE_SPEED,
      ACCEL,
      TURN_RATE
    );
    if (distance < STATION_DESPAWN_RANGE) {
      this.state = 'finished';
      this.finished = true;
    }
  }

  private enterCooldown(): void {
    this.state = 'cooldown';
    this.cooldownTimer = COOLDOWN_TIME;
  }

  private enterReturning(): void {
    this.ctx.claims.release(this.ctx.ship);
    this.ctx.beam?.hide();
    this.target = null;
    this.state = 'returning';
  }

  private fire(targetPos: THREE.Vector3): void {
    this.ctx.ship.object3D.updateMatrixWorld();
    _muzzleWorld
      .copy(MUZZLE_OFFSET)
      .applyMatrix4(this.ctx.ship.object3D.matrixWorld);
    _shotDir.copy(targetPos).sub(_muzzleWorld).normalize();
    this.ctx.hostileProjectiles.fire(
      _muzzleWorld,
      _shotDir,
      PROJECTILE_SPEED,
      MINER_DAMAGE
    );
  }

  private pickTarget(): void {
    const shipPos = this.ctx.ship.object3D.position;
    let best: Asteroid | null = null;
    let bestScore = Infinity;

    for (const entity of this.ctx.entities.all().values()) {
      if (!(entity instanceof Asteroid)) continue;
      if (!entity.isAlive) continue;
      if (this.ctx.claims.isClaimed(entity)) continue;
      const distSq = entity.object3D.position.distanceToSquared(shipPos);
      if (distSq > TARGET_SEARCH_RANGE_SQ) continue;

      if (distSq < bestScore) {
        bestScore = distSq;
        best = entity;
      }
    }

    if (best && this.ctx.claims.claim(this.ctx.ship, best)) {
      this.target = best;
    }
  }
}
